import { useCallback, useEffect, useRef, useState } from 'react';

export type AsyncState<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
  /** Replace the data locally, e.g. with a mutation's response. */
  setData: (data: T) => void;
};

/**
 * Loads data on mount and whenever `deps` change; `pollMs` refreshes in the
 * background (order tracking, shipper offers) without flashing the loader.
 */
export const useAsync = <T>(load: () => Promise<T>, deps: unknown[] = [], options: { pollMs?: number } = {}): AsyncState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const run = useCallback(async (background: boolean) => {
    if (!background) setLoading(true);
    try {
      setData(await loadRef.current());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Có lỗi xảy ra');
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void run(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (!options.pollMs) return;
    const id = setInterval(() => void run(true), options.pollMs);
    return () => clearInterval(id);
  }, [options.pollMs, run]);

  return { data, error, loading, reload: () => run(false), setData };
};

/** Wraps a mutation with pending/error state so buttons can show a spinner and the error inline. */
export const useAction = <A extends unknown[], R>(action: (...args: A) => Promise<R>) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      setPending(true);
      setError(null);
      try {
        return await action(...args);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Có lỗi xảy ra');
        return undefined;
      } finally {
        setPending(false);
      }
    },
    [action],
  );
  return { run, pending, error, clearError: () => setError(null) };
};
